import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa6';
import { SiGmail, SiTelegram } from 'react-icons/si';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  CheckGlyph,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';

import { Avatar, TEAM, useB2bTranslation } from './kit';

// Real images and a clip made on Easy-Peasy.AI (media template previews).
const MEDIA = {
  video:
    'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/video_final_3cc287a1-ad7b-4daa-9e28-41a42251015b-optimized.mp4',
  // The clip's first second, so the tile is never an empty box.
  poster: '/images/b2b/video-ad-poster.webp',
  perfume:
    'https://media.easy-peasy.ai/landing/media-templates/trend-floral-perfume-campaign-0922c.webp',
  wine: 'https://media.easy-peasy.ai/landing/media-templates/trend-bottle-of-red-wine-0921c.webp',
  illustration:
    'https://media.easy-peasy.ai/landing/media-templates/trend-flat-vector-0921c.webp',
};

export default function TeamsSuite() {
  const { t } = useB2bTranslation();

  return (
    <section id='tools' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('Every AI tool your team needs.')}
          tail={t('One login, one bill.')}
          lead={t(
            'Stop paying for a separate tool for every job. Writing, research, images, video, voice, meetings and customer support all live in one workspace.',
          )}
        />

        <div className='mt-16 grid gap-5 lg:grid-cols-3'>
          <Tile
            href='/marky'
            className='lg:col-span-2'
            title={t('Marky Agent does the work')}
            detail={t(
              'Give it a goal and it researches, writes, analyzes data and builds slides and websites, step by step.',
            )}
          >
            <AgentTask />
          </Tile>

          <Tile
            href='/ai-image-generator'
            className='lg:row-span-2'
            fill
            title={t('Images and video for every campaign')}
            detail={t(
              'Product shots, ads and social videos from the leading image and video models.',
            )}
          >
            <MediaMosaic />
          </Tile>

          <Tile
            href='/text-to-speech'
            title={t('Voiceovers in natural voices')}
            detail={t(
              'Turn scripts into narration for videos, training and ads, or use a clone of your own voice.',
            )}
          >
            <VoiceRow />
          </Tile>

          <Tile
            href='/ai-transcription'
            title={t('Notes from every meeting')}
            detail={t(
              'Record or upload a call and get a transcript, a summary and the action items.',
            )}
          >
            <MeetingNotes />
          </Tile>

          <Tile
            href='/templates'
            title={t('300+ ready-made templates')}
            detail={t(
              'Job descriptions, sales emails, reports, SOPs and more, ready in seconds.',
            )}
          >
            <TemplateChips />
          </Tile>

          <Tile
            href='/chatbots'
            className='lg:col-span-2'
            title={t('AI agents that answer your customers')}
            detail={t(
              'Train an agent on your documents and put it on your website, WhatsApp, email and the phone.',
            )}
          >
            <CustomerAgent />
          </Tile>
        </div>
      </div>
    </section>
  );
}

function Tile({
  href,
  title,
  detail,
  className,
  fill = false,
  children,
}: {
  href: string;
  title: string;
  detail: string;
  className?: string;
  /** Let the visual grow to the tile's full height. */
  fill?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={clsxm(
        'group relative flex min-w-0 flex-col gap-7 rounded-[28px] p-7 sm:p-8',
        tone.surface,
        tone.line,
        className,
      )}
    >
      <div className='pe-12'>
        <h3 className={clsxm('text-lg font-semibold', tone.ink)}>
          {/* The whole tile is the link; the title names it for assistive tech. */}
          <Link
            href={href}
            className='after:absolute after:inset-0 after:rounded-[28px] focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-primary-600'
          >
            {title}
          </Link>
        </h3>
        <p className={clsxm('mt-2 max-w-md text-[15px] leading-7', tone.body)}>
          {detail}
        </p>
      </div>
      <div
        aria-hidden='true'
        className={fill ? 'flex flex-1 flex-col' : 'mt-auto'}
      >
        {children}
      </div>
      <span
        aria-hidden='true'
        className='absolute end-7 top-7 flex h-8 w-8 items-center justify-center rounded-full text-[#8a8aa1] ring-1 ring-[#0f0f28]/10 transition duration-300 group-hover:bg-primary-600 group-hover:text-white group-hover:ring-primary-600 sm:end-8 sm:top-8 dark:ring-white/15'
      >
        <ArrowGlyph className='h-3.5 w-3.5 -rotate-45 rtl:rotate-45' />
      </span>
    </div>
  );
}

const panel = 'rounded-2xl bg-[#f7f7fa] dark:bg-white/[0.04]';

function AgentTask() {
  const { t } = useB2bTranslation();
  const phases: { label: string; state: 'done' | 'active' | 'next' }[] = [
    { label: t('Searched 24 sources'), state: 'done' },
    { label: t('Compared 6 competitors in a table'), state: 'done' },
    { label: t('Writing the summary'), state: 'active' },
    { label: t('Building 8 slides'), state: 'next' },
  ];

  return (
    <div className={clsxm(panel, 'grid gap-5 p-5 sm:grid-cols-[1fr_auto]')}>
      <div className='min-w-0'>
        <div className='flex items-center gap-3'>
          <Image
            src='/apple-touch-icon.png'
            alt=''
            width={28}
            height={28}
            className='h-7 w-7 flex-none rounded-full bg-white ring-1 ring-black/[0.06]'
          />
          <p
            className={clsxm(
              'min-w-0 text-sm font-semibold leading-snug',
              tone.ink,
            )}
          >
            {t('Competitor pricing report')}
          </p>
          <span className='ms-auto flex flex-none items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-semibold text-primary-700 dark:bg-primary-400/15 dark:text-primary-300'>
            <span className='h-1.5 w-1.5 animate-pulse rounded-full bg-primary-500 motion-reduce:animate-none' />
            {t('Working')}
          </span>
        </div>
        <ol className='mt-4 space-y-2.5'>
          {phases.map(({ label, state }) => (
            <li
              key={label}
              className={clsxm(
                'flex items-center gap-2.5 text-[13px]',
                state === 'next' ? tone.quiet : tone.ink,
              )}
            >
              {state === 'done' ? (
                <span className='flex h-4 w-4 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
                  <CheckGlyph className='h-2.5 w-2.5' />
                </span>
              ) : state === 'active' ? (
                <span className='h-4 w-4 flex-none animate-spin rounded-full border-2 border-primary-200 border-t-primary-600 motion-reduce:animate-none dark:border-primary-400/30 dark:border-t-primary-300' />
              ) : (
                <span className='h-4 w-4 flex-none rounded-full border-2 border-[#0f0f28]/15 dark:border-white/20' />
              )}
              <span className='truncate'>{label}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className='hidden w-44 flex-col justify-between rounded-xl bg-white p-3 ring-1 ring-black/[0.06] sm:flex dark:bg-white/[0.06] dark:ring-white/10'>
        <p className={clsxm('text-[11px] font-semibold', tone.quiet)}>
          {t('Price per seat')}
        </p>
        <div className='mt-3 flex h-20 items-end gap-2'>
          {[46, 72, 58, 88, 64, 38].map((height, index) => (
            <span
              key={index}
              className={clsxm(
                'flex-1 rounded-t-md',
                index === 3
                  ? 'bg-primary-500'
                  : 'bg-[#dcdcec] dark:bg-white/15',
              )}
              style={{ height: `${height}%` }}
            />
          ))}
        </div>
        <p className={clsxm('mt-2 text-[11px]', tone.quiet)}>
          {t('6 competitors')}
        </p>
      </div>
    </div>
  );
}

/** Plays the clip only while it is on screen, and never for reduced motion. */
function LazyClip({
  src,
  poster,
  className,
}: {
  src: string;
  poster: string;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  // The source is attached the first time the clip comes near the screen,
  // then it plays while visible and pauses when scrolled away.
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
        if (entry.isIntersecting) setLoaded(true);
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node || !loaded) return;
    if (visible) node.play().catch(() => undefined);
    else node.pause();
  }, [visible, loaded]);

  return (
    <video
      ref={ref}
      className={className}
      src={loaded ? src : undefined}
      poster={poster}
      muted
      loop
      playsInline
      preload='none'
    />
  );
}

function MediaMosaic() {
  const { t } = useB2bTranslation();
  const cell =
    'relative min-h-0 overflow-hidden rounded-2xl bg-[#ececf3] ring-1 ring-black/[0.05] dark:bg-white/[0.06] dark:ring-white/10';

  return (
    <div className='grid flex-1 grid-cols-2 grid-rows-[minmax(10rem,1.15fr)_minmax(8rem,1fr)_minmax(8rem,1fr)] gap-3 lg:min-h-[26rem]'>
      <div className={clsxm(cell, 'col-span-2')}>
        <LazyClip
          src={MEDIA.video}
          poster={MEDIA.poster}
          className='absolute inset-0 h-full w-full object-cover'
        />
        <span className='absolute start-3 top-3 flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur'>
          <span className='h-1.5 w-1.5 rounded-full bg-red-500' />
          {t('Video ad')}
        </span>
      </div>
      <div className={clsxm(cell, 'row-span-2')}>
        <Image
          src={MEDIA.perfume}
          alt=''
          fill
          sizes='(min-width: 1024px) 190px, 45vw'
          className='object-cover'
        />
      </div>
      <div className={cell}>
        <Image
          src={MEDIA.wine}
          alt=''
          fill
          sizes='(min-width: 1024px) 190px, 45vw'
          className='object-cover'
        />
      </div>
      <div className={cell}>
        <Image
          src={MEDIA.illustration}
          alt=''
          fill
          sizes='(min-width: 1024px) 190px, 45vw'
          className='object-cover'
        />
      </div>
    </div>
  );
}

const WAVE = [
  4, 8, 14, 10, 18, 26, 16, 30, 22, 12, 24, 34, 20, 28, 14, 22, 30, 18, 10, 16,
  24, 12, 8, 14, 6,
];

function VoiceRow() {
  const { t } = useB2bTranslation();

  return (
    <div className={clsxm(panel, 'p-4')}>
      <div className='flex items-center gap-3'>
        <span className='flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary-600 text-white shadow-[0_8px_20px_-8px_rgba(79,70,229,0.8)]'>
          <svg
            viewBox='0 0 20 20'
            fill='currentColor'
            className='h-4 w-4 rtl:-scale-x-100'
          >
            <path d='M6.3 2.84A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.27l9.34-5.89a1.5 1.5 0 000-2.54L6.3 2.84z' />
          </svg>
        </span>
        <div className='flex h-9 flex-1 items-center gap-[3px] overflow-hidden'>
          {WAVE.map((height, index) => (
            <span
              key={index}
              className='w-[3px] flex-none rounded-full bg-primary-400/80'
              style={{ height: `${height}px` }}
            />
          ))}
        </div>
      </div>
      <div className='mt-3 flex flex-wrap gap-2'>
        {[t('Warm narrator'), t('Your cloned voice')].map((voice, index) => (
          <span
            key={voice}
            className={clsxm(
              'rounded-full px-2.5 py-1 text-xs font-medium',
              index === 0
                ? 'bg-white text-[#10101c] ring-1 ring-black/[0.06] dark:bg-white/10 dark:text-white dark:ring-white/10'
                : tone.quiet,
            )}
          >
            {voice}
          </span>
        ))}
      </div>
    </div>
  );
}

function MeetingNotes() {
  const { t } = useB2bTranslation();

  return (
    <div className={clsxm(panel, 'p-4')}>
      <div className='flex items-center justify-between gap-3'>
        <p className={clsxm('truncate text-[13px] font-semibold', tone.ink)}>
          {t('Weekly sales sync')}
        </p>
        <span className={clsxm('flex-none text-xs tabular-nums', tone.quiet)}>
          32:14
        </span>
      </div>
      <p
        className={clsxm(
          'mt-3 text-[11px] font-semibold uppercase tracking-wide',
          tone.quiet,
        )}
      >
        {t('Action items')}
      </p>
      <ul className='mt-2 space-y-2'>
        {[
          {
            who: TEAM.daniel,
            what: t('Send the new price list to Northfield'),
          },
          { who: TEAM.arjun, what: t('Approve the Q4 discount') },
        ].map(({ who, what }) => (
          <li key={what} className='flex items-center gap-2.5'>
            <Avatar person={who} size='sm' />
            <span className={clsxm('truncate text-[13px]', tone.body)}>
              {what}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TemplateChips() {
  const { t } = useB2bTranslation();
  const templates = [
    t('Job description'),
    t('Sales email'),
    t('Meeting agenda'),
    t('Product description'),
    t('SOP'),
    t('Press release'),
  ];

  return (
    <div className='flex flex-wrap gap-2'>
      {templates.map((template, index) => (
        <span
          key={template}
          className={clsxm(
            'rounded-full px-3 py-1.5 text-[13px] font-medium',
            index === 0
              ? 'bg-primary-600 text-white'
              : 'bg-[#f1f1f6] text-[#40405a] dark:bg-white/[0.06] dark:text-[#c4c4dc]',
          )}
        >
          {template}
        </span>
      ))}
    </div>
  );
}

function CustomerAgent() {
  const { t } = useB2bTranslation();

  return (
    <div
      className={clsxm(
        panel,
        'grid gap-4 p-5 sm:grid-cols-[1fr_auto] sm:items-end',
      )}
    >
      <div className='w-full max-w-sm space-y-2.5 text-[13px] leading-snug'>
        <p className='ms-auto w-fit max-w-[85%] rounded-2xl rounded-ee-md bg-white px-3.5 py-2 text-[#10101c] ring-1 ring-black/[0.05] dark:bg-white/10 dark:text-white dark:ring-white/10'>
          {t('Do you ship to Germany?')}
        </p>
        <p className='w-fit max-w-[85%] rounded-2xl rounded-es-md bg-[#1d1c26] px-3.5 py-2 text-white dark:bg-[#2b2a38]'>
          {t(
            'Yes! Orders to Germany arrive in 3 to 5 days, and shipping is free over €50.',
          )}
        </p>
      </div>
      <div className='flex items-center gap-2 sm:flex-col sm:items-stretch'>
        {[
          {
            icon: <FaWhatsapp className='h-3.5 w-3.5 text-[#25d366]' />,
            label: 'WhatsApp',
          },
          {
            icon: <SiGmail className='h-3.5 w-3.5 text-[#ea4335]' />,
            label: 'Gmail',
          },
          {
            icon: <SiTelegram className='h-3.5 w-3.5 text-[#26a5e4]' />,
            label: 'Telegram',
          },
        ].map(({ icon, label }) => (
          <span
            key={label}
            className={clsxm(
              'flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium ring-1 ring-black/[0.06] dark:bg-white/10 dark:ring-white/10',
              tone.body,
            )}
          >
            {icon}
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
